/** The money card's Stripe figures, and the remainder left unreconciled against our record. */
import { reconcileBase, reconcileGapCents } from '../reviewMoneyModel';
import { formatMoney, stripeStatusLabel } from '../reviewQueueModel';
import type { ReviewDetail, ReviewStripeSnapshot } from '../reviewTypes';

import Row from './ReviewMoneyRow';

export default function StripeRows({
  detail,
  stripe,
  currency,
}: {
  detail: ReviewDetail;
  stripe: ReviewStripeSnapshot;
  currency: string;
}) {
  // The SAME base the sentence uses, or the number and the words disagree about
  // the same payment: our recorded cash on a booking, the order on a checkout.
  const gap = reconcileGapCents(detail);
  const base = reconcileBase(detail);
  return (
    <>
      <Row label="Authorized" value={formatMoney(stripe.amountCents, currency)} />
      <Row
        label="Captured"
        value={formatMoney(stripe.capturedCents, currency)}
        tone={stripe.capturedCents ? 'var(--nb-primary-2)' : 'var(--nb-faint)'}
      />
      {stripe.refundedCents > 0 && (
        <Row label="Refunded" value={`−${formatMoney(stripe.refundedCents, currency)}`} />
      )}
      {/* U10: Stripe's raw enum (`requires_payment_method`) is not a sentence, and
          the reader is an operator deciding what to do — not someone who knows
          Stripe's state machine. The API keeps sending the verbatim status so the
          audit trail records what Stripe actually said; the mapping is display-only. */}
      <Row label="Stripe status" value={stripeStatusLabel(stripe.status)} />
      <div style={{ borderTop: '1px solid var(--nb-border)', paddingTop: 8 }}>
        <Row
          // Named for what it is measured against, because "Unreconciled" alone
          // never said. A refunded booking reconciles against our recorded cash
          // and would never again match the order it was billed for.
          label={`Unreconciled vs ${base.label === 'order' ? 'order' : 'recorded paid'}`}
          value={`${gap < 0 ? '+' : ''}${formatMoney(Math.abs(gap), currency)}`}
          tone={gap === 0 ? 'var(--nb-primary-2)' : 'var(--nb-warn)'}
        />
      </div>
    </>
  );
}
